import React, { useEffect, useRef } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import {
  alpha,
  Breadcrumbs,
  Button,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
} from "@mui/material";
import Box from "@mui/material/Box";
import GenieText from "../../../components/core/genie-text/GenieText";
import { a11yProps } from "../../pages/orders/functions";
import { TabPanel } from "./TabPanel";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { theme } from "../../../components/special/genie-theme/genie-theme";
import useProduct from "../../../queries/routes/useProduct";
import { formatCurrency } from "../../../utils/currency";
import useCurrentCart from "../../../queries/routes/useCurrentCart";
import useUser from "../../../queries/routes/useUser";
import useAddLineItem from "../../../queries/routes/useAddLineItem";
import useDeleteLineItem from "../../../queries/routes/useDeleteLineItem";
import Chip from "@mui/material/Chip";
import GenieSpinner from "../../../components/core/genie-spinner/GenieSpinner";

export const MAX_QUANTITY = 80;
const quantityMap = new Array(MAX_QUANTITY).fill(0).map((_, i) => i);

const DESKTOP_PRODUCT_TILE_SIZE = 400 + 64;

function Pdp() {
  const { org_id, product_id } = useParams();

  const { data: product } = useProduct(product_id!);
  const { data: user } = useUser();

  const currentOrg = user?.organizations[0];

  const { data: cart } = useCurrentCart(org_id!, user?.organizations[0].id!);
  const { mutate: addLineItem, isLoading: isAddLineItemLoading } =
    useAddLineItem(
      cart?.id || "",
      org_id!,
      user?.purchasing_organizations[0].organization.id!
    );
  const currentLineItem = cart?.line_items.find((lineItem) => {
    return lineItem.product.id === product?.id;
  });
  const { mutate: deleteLineItem } = useDeleteLineItem(
    currentLineItem?.id || "",
    org_id!,
    user?.purchasing_organizations[0].organization.id!
  );

  useEffect(() => {
    if (currentLineItem) {
      setQuantity(currentLineItem.quantity);
    }
  }, [currentLineItem]);

  const [selectedThumbnail, setSelectedThumbnail] = React.useState(0);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [quantity, setQuantity] = React.useState(
    currentLineItem ? currentLineItem.quantity : 0
  );
  const handleQuantityChange = (event: any) => {
    setQuantity(event.target.value);
  };

  const [tab, setTab] = React.useState(0);
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  const thumbnailContainerRef = useRef<HTMLDivElement>(null);

  if (!product || !cart) {
    return <GenieSpinner headerOffset={81} />;
  }

  return (
    <Container maxWidth="lg" sx={{ mt: isMobile ? 2 : 4 }}>
      {currentOrg && (
        <Breadcrumbs aria-label="pdp-breadcrumb">
          <Link
            variant={isMobile ? "body2" : "body1"}
            underline="hover"
            color="inherit"
            component={RouterLink}
            to={`/storefront/${org_id}`}
          >
            Genie
          </Link>
          <Link
            component={RouterLink}
            variant={isMobile ? "body2" : "body1"}
            underline="hover"
            color="inherit"
            to={`/storefront/${org_id}`}
          >
            {currentOrg.name}
          </Link>
          <Typography
            variant={isMobile ? "body2" : "body1"}
            color="text.primary"
          >
            {product.name}
          </Typography>
        </Breadcrumbs>
      )}

      {isMobile ? (
        <>
          <Box
            component={"img"}
            height={"100%"}
            width={"100%"}
            sx={{ backgroundSize: "contain", aspectRatio: "1 / 1" }}
            src={
              product.media.length > 0
                ? product.media[selectedThumbnail].media.cloudinary_url
                : "https://source.unsplash.com/random/502x502"
            }
            alt="Product thumbnail"
          />
          <Box
            sx={{ overflowX: "auto", overflowY: "auto", whiteSpace: "nowrap" }}
          >
            {product.media.map((image, index) => (
              <Box
                component={"img"}
                key={index}
                width={80}
                height={80}
                onClick={() => setSelectedThumbnail(index)}
                sx={{
                  backgroundImage: `url(${image.media.cloudinary_url})`,
                  mr: 2,
                  display: "inline-block",
                  backgroundSize: "contain",
                  cursor: "pointer",
                  boxShadow:
                    selectedThumbnail === index
                      ? `inset 0px 0px 0 2px ${theme.palette.primary.main}`
                      : `none`,
                }}
              />
            ))}
          </Box>
          <Box flexGrow={1}>
            <Box mb={1} height={"32px"}>
              {product?.order_unit?.unit && (
                <Chip
                  variant={"outlined"}
                  color={"primary"}
                  label={product.order_unit.unit}
                />
              )}
            </Box>
            <GenieText header paragraph fontWeight={"bold"}>
              {product.name}
            </GenieText>
            <GenieText header paragraph fontSize={"small"}>
              {formatCurrency(product.unit_price_in_cents)} Wholesale |{" "}
              {formatCurrency(product.msrp_in_cents)} Retail
            </GenieText>

            <FormControl fullWidth sx={{ mt: 1 }}>
              <InputLabel id="quantity-select-label">Quantity</InputLabel>
              <Select
                labelId="quantity-select-label"
                id="quantity-select"
                value={quantity}
                required
                size={isMobile ? "small" : "medium"}
                label="Quantity"
                onChange={handleQuantityChange}
              >
                {quantityMap.map((q) => (
                  <MenuItem key={q} value={q}>
                    {q}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant={"outlined"}
              sx={{ mt: 1 }}
              size={isMobile ? "small" : "large"}
              fullWidth
              onClick={() => {
                if (quantity === 0) {
                  if (currentLineItem) {
                    deleteLineItem(currentLineItem.id);
                  }
                } else {
                  addLineItem({
                    product_id: product.id,
                    quantity: quantity,
                  });
                }
              }}
            >
              {isAddLineItemLoading ? (
                <CircularProgress size={16} />
              ) : currentLineItem ? (
                "Update Quantity"
              ) : (
                "Add to Cart"
              )}
            </Button>
          </Box>
        </>
      ) : (
        <Box mt={2} display={"flex"}>
          <Box>
            <Box display={"flex"} justifyContent={"center"}>
              <ExpandLess
                fontSize={"large"}
                cursor={"pointer"}
                onClick={() => {
                  thumbnailContainerRef.current?.scrollBy({
                    top: -108,
                    left: 0,
                    behavior: "smooth",
                  });
                }}
              />
            </Box>
            <Box
              width={100}
              height={400 + 32}
              sx={{ overflow: "hidden" }}
              ref={thumbnailContainerRef}
            >
              {product.media.map((image, index) => (
                <Box
                  component={"img"}
                  key={index}
                  width={100}
                  height={100}
                  onClick={() => setSelectedThumbnail(index)}
                  sx={{
                    backgroundImage: `url(${image.media.cloudinary_url})`,
                    mb: 1,
                    backgroundSize: "contain",
                    cursor: "pointer",
                    boxShadow:
                      selectedThumbnail === index
                        ? `inset 0px 0px 0 4px ${theme.palette.primary.main}`
                        : `none`,
                  }}
                />
              ))}
            </Box>
            <Box display={"flex"} justifyContent={"center"}>
              <ExpandMore
                fontSize={"large"}
                cursor={"pointer"}
                onClick={() => {
                  thumbnailContainerRef.current?.scrollBy({
                    top: 108,
                    left: 0,
                    behavior: "smooth",
                  });
                }}
              />
            </Box>
          </Box>
          <Box>
            <Box
              ml={2}
              component={"img"}
              height={"auto"}
              width={"100%"}
              maxWidth={DESKTOP_PRODUCT_TILE_SIZE}
              maxHeight={DESKTOP_PRODUCT_TILE_SIZE}
              minWidth={DESKTOP_PRODUCT_TILE_SIZE}
              minHeight={DESKTOP_PRODUCT_TILE_SIZE}
              bgcolor={alpha(theme.palette.primary.light, 0.1)}
              sx={{ backgroundSize: "contain", aspectRatio: "1 / 1" }}
              src={
                product.media.length > 0
                  ? product.media[selectedThumbnail].media.cloudinary_url
                  : "https://source.unsplash.com/random/502x502"
              }
              alt="Product thumbnail"
            />
          </Box>
          <Box ml={3} flexGrow={1} minWidth={"240px"}>
            <Box mb={1} height={"32px"}>
              {product?.order_unit?.unit && (
                <Chip
                  color={"primary"}
                  variant={"outlined"}
                  label={product.order_unit.unit}
                />
              )}
            </Box>
            <GenieText header paragraph fontWeight={"bold"}>
              {product.name}
            </GenieText>

            <GenieText header paragraph fontSize={"small"}>
              {formatCurrency(product.unit_price_in_cents)} Wholesale |{" "}
              {formatCurrency(product.msrp_in_cents)} Retail
            </GenieText>

            <FormControl fullWidth sx={{ mt: 4 }}>
              <InputLabel id="quantity-select-label">Quantity</InputLabel>
              <Select
                labelId="quantity-select-label"
                id="quantity-select"
                value={quantity}
                required
                label="Quantity"
                onChange={handleQuantityChange}
              >
                {quantityMap.map((q) => (
                  <MenuItem key={q} value={q}>
                    {q}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant={"outlined"}
              sx={{ mt: 4 }}
              size={"large"}
              fullWidth
              onClick={() => {
                if (quantity === 0) {
                  if (currentLineItem) {
                    deleteLineItem(currentLineItem.id);
                  }
                } else {
                  addLineItem({
                    product_id: product.id,
                    quantity: quantity,
                  });
                }
              }}
            >
              {isAddLineItemLoading ? (
                <CircularProgress size={26.5} />
              ) : currentLineItem ? (
                "Update Quantity"
              ) : (
                "Add to Cart"
              )}
            </Button>
          </Box>
        </Box>
      )}
      <Box sx={{ borderBottom: 1, mt: 4, borderColor: "divider" }}>
        <Tabs value={tab} onChange={handleTabChange} aria-label="pdp-tabs">
          <Tab label="Description" {...a11yProps(0)} />
          <Tab label="Dimensions" {...a11yProps(1)} />
        </Tabs>
      </Box>

      <TabPanel value={tab} index={0}>
        <Box sx={{ whiteSpace: "pre-line" }}>{product.description}</Box>
      </TabPanel>
      <TabPanel value={tab} index={1}>
        <Box>
          Weight: {product.product_physical_measurements.weight}{" "}
          {product.product_physical_measurements.weight_unit?.unit}
        </Box>
        {/* <Box>
          Length: {product.product_physical_measurements.length}{" "}
          {product.product_physical_measurements.dimension_unit?.unit}
        </Box>
        <Box>
          Width: {product.product_physical_measurements.width}{" "}
          {product.product_physical_measurements.dimension_unit?.unit}
        </Box>
        <Box>
          Height: {product.product_physical_measurements.height}{" "}
          {product.product_physical_measurements.dimension_unit?.unit}
        </Box> */}
      </TabPanel>
    </Container>
  );
}

export default Pdp;
